import { Fragment } from 'react';

import { SourceAttribution } from '@/components/branding/SourceAttribution';
import { TSG_SOURCE, type Source } from '@/data/sources';
import type { WhyThisMatters } from '@/api/dashboard';

import styles from './WhyThisMattersCard.module.css';

interface WhyThisMattersCardProps {
  data: WhyThisMatters;
}

/**
 * Render the complaint drill-down "Why This Matters" card per the v14 mockup.
 * Body copy supports `**bold**` markdown for inline emphasis; risks render as
 * coral pills; the source line mounts SourceAttribution so future partner
 * additions need no changes here.
 */
export function WhyThisMattersCard({ data }: WhyThisMattersCardProps) {
  const sources = mapSources(data.source);

  return (
    <section className={styles.card} aria-label="Clinical context">
      <header className={styles.header}>
        <span className={styles.eyebrow}>{data.title}</span>
      </header>

      <p className={styles.body}>{renderBoldMarkdown(data.body)}</p>

      {data.risks.length > 0 && (
        <ul className={styles.risks} aria-label="Documented misses to guard against">
          {data.risks.map((risk) => (
            <li key={risk} className={styles.riskPill}>{risk}</li>
          ))}
        </ul>
      )}

      {sources.length > 0 && (
        <footer className={styles.footer}>
          <SourceAttribution sources={sources} density="compact" label="Source" />
        </footer>
      )}
    </section>
  );
}

/**
 * Map the API source enum to a Source[] from the catalog.
 * `tsg`       -> TSG attribution chip.
 * `ai-draft`  -> no chip (the UI shows an AI-Draft note elsewhere; mockup
 *                renders no partner logo for AI-generated draft copy).
 */
function mapSources(source: WhyThisMatters['source']): Source[] {
  switch (source) {
    case 'tsg':
      return [TSG_SOURCE];
    case 'ai-draft':
      return [];
    default:
      return [];
  }
}

/**
 * Tiny `**bold**` parser. We deliberately keep this in-component instead of
 * pulling a Markdown library: the API contract is narrow (bold only) and the
 * footprint isn't worth a runtime dependency. Anything that isn't a bold span
 * is rendered as plain text.
 */
function renderBoldMarkdown(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}
